import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import {
  AlertTriangle,
  Battery,
  Droplets,
  Settings2,
  Thermometer,
  Trash2,
  LoaderPinwheel,
  Activity,
  Wifi,
  WifiOff,
  RefreshCw,
  Bell,
  BellOff,
  Radio,
  Clock,
  Users,
  MapPin,
  Gauge,
  SlidersHorizontal,
  X,
  CircleDot,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import Navbar from "../../components/ui/Navbar";
import {
  createEnvironmentalSensor,
  deleteEnvironmentalSensor,
  deleteTuyaCredentials,
  getAreas,
  getClientes,
  getEnvironmentalDashboard,
  getEnvironmentalHistory,
  getEnvironmentalAlertUsers,
  getEnvironmentalSensors,
  getEquiposByFilters,
  getSedesByCliente,
  getTuyaCredentials,
  pollEnvironmentalSensor,
  saveTuyaCredentials,
  updateEnvironmentalSensor,
} from "../../services/environmentalApi";

const defaultSensorForm = {
  name: "",
  deviceId: "",
  sensorType: "temperature_humidity",
  cliente: "",
  sede: "",
  area: "",
  equipo: "",
  pollingIntervalSeconds: 10,
  alertRanges: {
    temperature: { min: 18, max: 27 },
    humidity: { min: 35, max: 65 },
  },
  alertsEnabled: true,
  alertRecipients: [],
};

const defaultCredsForm = {
  clientId: "",
  clientSecret: "",
  region: "us",
};

const sensorTypes = [
  { value: "temperature_humidity", label: "Temperatura y humedad" },
  { value: "movement", label: "Movimiento" },
  { value: "control", label: "Control" },
];

const sensorColors = [
  "#ef4444",
  "#3b82f6",
  "#10b981",
  "#a855f7",
  "#f59e0b",
  "#06b6d4",
];

const getBatteryFromPayload = (payload = []) => {
  if (!Array.isArray(payload)) return null;
  const batteryPoint = payload.find((item) =>
    ["battery_percentage", "battery", "va_battery", "battery_value"].includes(
      item?.code,
    ),
  );

  const batteryValue = Number(batteryPoint?.value);
  if (!Number.isFinite(batteryValue)) return null;
  return batteryValue > 100
    ? Math.round(batteryValue / 10)
    : Math.round(batteryValue);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const KpiCard = ({ icon: Icon, iconBg, value, label, sublabel }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
      <Icon className="h-6 w-6 text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm font-medium text-gray-600">{label}</p>
      {sublabel && <p className="text-xs text-gray-400">{sublabel}</p>}
    </div>
  </div>
);

const BatteryBar = ({ level }) => {
  const color = level > 50 ? "bg-green-500" : level > 20 ? "bg-amber-400" : "bg-red-500";
  return (
    <div className="flex items-center gap-1.5">
      <Battery className="h-3.5 w-3.5 text-gray-400" />
      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden w-14">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${level}%` }} />
      </div>
      <span className="text-xs text-gray-500">{level}%</span>
    </div>
  );
};

const FormField = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white disabled:bg-gray-50 disabled:text-gray-400";
const selectCls = inputCls;

// ─── Main Component ───────────────────────────────────────────────────────────

const EnvironmentalMonitoringPage = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [sensors, setSensors] = useState([]);
  const [history, setHistory] = useState([]);
  const [sensorId, setSensorId] = useState("");
  const [from, setFrom] = useState(
    dayjs().subtract(24, "hour").format("YYYY-MM-DDTHH:mm"),
  );
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DDTHH:mm"));
  const [loading, setLoading] = useState(false);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingActive, setLoadingActive] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [deleteConfirmWord, setDeleteConfirmWord] = useState({});
  const [alertUsers, setAlertUsers] = useState([]);
  const [deleteModalSensor, setDeleteModalSensor] = useState(null);

  const [clients, setClients] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [equipos, setEquipos] = useState([]);

  const [credentialsForm, setCredentialsForm] = useState(defaultCredsForm);
  const [credentialsStatus, setCredentialsStatus] = useState(null);
  const [sensorForm, setSensorForm] = useState(defaultSensorForm);

  const loadDashboard = async () => {
    const [dashRes, sensorsRes, historyRes] = await Promise.all([
      getEnvironmentalDashboard(),
      getEnvironmentalSensors(),
      getEnvironmentalHistory({ sensorId: sensorId || undefined, from, to }),
    ]);
    setDashboard(dashRes.data);
    setSensors(sensorsRes.data);
    setHistory(historyRes.data);
  };

  const loadParamData = async () => {
    const [clientsRes, areasRes, credRes, usersRes] = await Promise.all([
      getClientes(),
      getAreas(),
      getTuyaCredentials(),
      getEnvironmentalAlertUsers(),
    ]);

    setClients(clientsRes.data || []);
    setAreas(areasRes.data || []);
    setAlertUsers(usersRes.data || []);

    if (credRes.data) {
      setCredentialsStatus(credRes.data);
      setCredentialsForm((prev) => ({
        ...prev,
        clientId: credRes.data.clientId || "",
        region: credRes.data.region || "us",
      }));
    } else {
      setCredentialsStatus(null);
      setCredentialsForm(defaultCredsForm);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      await Promise.all([loadDashboard(), loadParamData()]);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "No se pudo cargar el módulo ambiental";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      loadDashboard().catch(() => {});
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [sensorId, from, to]);

  useEffect(() => {
    const fetchSedes = async () => {
      if (!sensorForm.cliente) {
        setSedes([]);
        return;
      }
      try {
        const sedesRes = await getSedesByCliente(sensorForm.cliente);
        setSedes(sedesRes.data || []);
      } catch {
        setSedes([]);
      }
    };

    fetchSedes();
  }, [sensorForm.cliente]);

  useEffect(() => {
    const fetchEquipos = async () => {
      if (!sensorForm.cliente || !sensorForm.sede || !sensorForm.area) {
        setEquipos([]);
        return;
      }

      try {
        const equiposRes = await getEquiposByFilters({
          cliente: sensorForm.cliente,
          sede: sensorForm.sede,
          area: sensorForm.area,
        });
        setEquipos(equiposRes.data || []);
      } catch {
        setEquipos([]);
      }
    };

    fetchEquipos();
  }, [sensorForm.cliente, sensorForm.sede, sensorForm.area]);

  const points = useMemo(() => {
    if (sensorId) {
      return history.map((item) => ({
        time: dayjs(item.measuredAt).format("DD/MM HH:mm"),
        temperature: item.temperature,
        humidity: item.humidity,
      }));
    }

    const groupedByTime = history.reduce((acc, item) => {
      const time = dayjs(item.measuredAt).format("DD/MM HH:mm");
      const sensorKey = String(item.sensor);
      const current = acc[time] || { time };
      current[sensorKey] = item.temperature;
      acc[time] = current;
      return acc;
    }, {});

    return Object.values(groupedByTime);
  }, [history, sensorId]);

  const selectedSensor = useMemo(
    () => sensors.find((sensor) => sensor._id === sensorId) || null,
    [sensorId, sensors],
  );

  const sensorAlerts = useMemo(
    () =>
      history
        .filter((item) => item.isOutOfRange)
        .slice(-10)
        .reverse(),
    [history],
  );

  const handleSaveCredentials = async () => {
    if (!credentialsForm.clientId || !credentialsForm.clientSecret) {
      toast.warning("Client ID y Client Secret son obligatorios");
      return;
    }

    setLoading(true);
    try {
      await saveTuyaCredentials(credentialsForm);
      toast.success("Configuración guardada");
      await loadParamData();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "No se pudieron guardar las credenciales";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCredentials = async () => {
    const ok = window.confirm(
      "¿Eliminar configuración actual? Luego podrás registrar una nueva.",
    );
    if (!ok) return;

    setLoading(true);
    try {
      await deleteTuyaCredentials();
      toast.success("Configuración eliminada");
      await loadParamData();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "No se pudo eliminar la configuración";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSensor = async () => {
    if (
      !sensorForm.name ||
      !sensorForm.deviceId ||
      !sensorForm.cliente ||
      !sensorForm.sede ||
      !sensorForm.area ||
      !sensorForm.sensorType
    ) {
      toast.warning("Completa los campos obligatorios del sensor");
      return;
    }

    setLoading(true);
    try {
      await createEnvironmentalSensor({
        ...sensorForm,
        equipo: sensorForm.equipo || null,
      });
      toast.success("Sensor registrado correctamente");
      setSensorForm(defaultSensorForm);
      await loadDashboard();
    } catch (error) {
      const msg =
        error?.response?.data?.message || "No se pudo registrar el sensor";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAlerts = async (sensor) => {
    if (sensor.sensorType !== "temperature_humidity") return;

    const tMin = window.prompt(
      "Temperatura mínima",
      String(sensor.alertRanges?.temperature?.min ?? 18),
    );
    const tMax = window.prompt(
      "Temperatura máxima",
      String(sensor.alertRanges?.temperature?.max ?? 27),
    );
    const hMin = window.prompt(
      "Humedad mínima",
      String(sensor.alertRanges?.humidity?.min ?? 35),
    );
    const hMax = window.prompt(
      "Humedad máxima",
      String(sensor.alertRanges?.humidity?.max ?? 65),
    );

    if ([tMin, tMax, hMin, hMax].some((v) => v === null)) return;

    setLoading(true);
    try {
      await updateEnvironmentalSensor(sensor._id, {
        alertRanges: {
          temperature: { min: Number(tMin), max: Number(tMax) },
          humidity: { min: Number(hMin), max: Number(hMax) },
        },
        alertsEnabled: true,
        alertRecipients: sensor.alertRecipients?.map((u) => u._id || u) || [],
        pollingIntervalSeconds: sensor.pollingIntervalSeconds,
        isActive: sensor.isActive,
      });
      toast.success("Alertas actualizadas");
      await loadDashboard();
    } catch (error) {
      const msg =
        error?.response?.data?.message || "No se pudieron actualizar alertas";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAlerts = async (sensor) => {
    setLoading(true);
    try {
      await updateEnvironmentalSensor(sensor._id, {
        alertRanges: {
          temperature: { min: 18, max: 27 },
          humidity: { min: 35, max: 65 },
        },
        alertsEnabled: false,
        alertRecipients: [],
        pollingIntervalSeconds: sensor.pollingIntervalSeconds,
        isActive: sensor.isActive,
      });
      toast.success("Alertas eliminadas para este sensor");
      await loadDashboard();
    } catch (error) {
      const msg =
        error?.response?.data?.message || "No se pudieron eliminar alertas";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRecipients = async (sensor, recipients) => {
    setLoadingAlerts(true);
    try {
      await updateEnvironmentalSensor(sensor._id, {
        alertRanges: sensor.alertRanges,
        alertsEnabled: sensor.alertsEnabled,
        alertRecipients: recipients,
        pollingIntervalSeconds: sensor.pollingIntervalSeconds,
        isActive: sensor.isActive,
      });
      await loadDashboard();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "No se pudieron actualizar usuarios de alertas",
      );
    } finally {
      setLoadingAlerts(false);
    }
  };

  const handleUpdatePollingInterval = async (sensor) => {
    const value = window.prompt(
      "Frecuencia de lectura en segundos (mínimo 10, máximo 3600)",
      String(sensor.pollingIntervalSeconds ?? 10),
    );

    if (value === null) return;

    const pollingIntervalSeconds = Number(value);
    if (
      !Number.isInteger(pollingIntervalSeconds) ||
      pollingIntervalSeconds < 10 ||
      pollingIntervalSeconds > 3600
    ) {
      toast.warning("Ingresa un número entero entre 10 y 3600 segundos");
      return;
    }

    setLoading(true);
    try {
      await updateEnvironmentalSensor(sensor._id, {
        alertRanges: sensor.alertRanges,
        alertsEnabled: sensor.alertsEnabled,
        alertRecipients: (sensor.alertRecipients || []).map((u) => u._id || u),
        pollingIntervalSeconds,
        isActive: sensor.isActive,
      });
      toast.success("Frecuencia de lectura actualizada");
      await loadDashboard();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "No se pudo actualizar la frecuencia de lectura",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSensorReading = async (sensor) => {
    setLoadingActive(true);
    try {
      await updateEnvironmentalSensor(sensor._id, {
        alertRanges: sensor.alertRanges,
        alertsEnabled: sensor.alertsEnabled,
        alertRecipients: (sensor.alertRecipients || []).map((u) => u._id || u),
        pollingIntervalSeconds: sensor.pollingIntervalSeconds,
        isActive: !sensor.isActive,
      });
      toast.success(
        !sensor.isActive
          ? "Lecturas automáticas activadas"
          : "Lecturas automáticas detenidas",
      );
      await loadDashboard();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "No se pudo cambiar el estado de lectura",
      );
    } finally {
      setLoadingActive(false);
    }
  };

  const handleDeleteSensor = async (sensorIdToDelete) => {
    const word = deleteConfirmWord[sensorIdToDelete] || "";
    setLoadingDelete(true);
    try {
      await deleteEnvironmentalSensor(sensorIdToDelete, word);
      toast.success("Sensor e historial eliminados");
      setDeleteConfirmWord((prev) => ({ ...prev, [sensorIdToDelete]: "" }));
      setDeleteModalSensor(null);
      await loadDashboard();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "No se pudo eliminar. Debes escribir 'confirmar'.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleFilter = async () => {
    setLoading(true);
    try {
      const res = await getEnvironmentalHistory({
        sensorId: sensorId || undefined,
        from,
        to,
      });
      setHistory(res.data);
    } catch (error) {
      const msg =
        error?.response?.data?.message || "No se pudo consultar el histórico";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePoll = async () => {
    if (!sensorId) {
      toast.info("Selecciona un sensor para forzar lectura");
      return;
    }

    setLoading(true);
    try {
      await pollEnvironmentalSensor(sensorId);
      toast.success("Lectura obtenida");
      await loadDashboard();
    } catch (error) {
      const msg = error?.response?.data?.message || "No se pudo leer el sensor";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ─── Loading screen ───────────────────────────────────────────────────────

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-6 pt-20 md:pt-28 flex items-center justify-center">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
              <LoaderPinwheel className="h-8 w-8 animate-spin text-blue-600" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-800">Cargando datos</p>
              <p className="text-sm text-gray-500 mt-1">Conectando con sensores IoT...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Tabs config ──────────────────────────────────────────────────────────

  const tabs = [
    { key: "dashboard", label: "Dashboard", icon: Gauge },
    { key: "param", label: "Parametrización", icon: SlidersHorizontal },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28 space-y-5">

        {/* ── Page Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-600 flex items-center justify-center shadow-sm">
              <Radio className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">Monitoreo IoT Ambiental</h1>
                <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  En vivo
                </span>
              </div>
              <p className="text-sm text-gray-500">Temperatura, humedad y alertas en tiempo real</p>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center bg-white border border-gray-200 rounded-xl p-1 shadow-sm gap-1">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === key
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Error banner ── */}
        {errorMsg && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">{errorMsg}</div>
            <button onClick={() => setErrorMsg("")} className="text-red-400 hover:text-red-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* ════════════════════════════════════════════
            DASHBOARD TAB
        ════════════════════════════════════════════ */}
        {activeTab === "dashboard" && (
          <div className="space-y-5">

            {/* KPI Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <KpiCard
                icon={Wifi}
                iconBg="bg-green-500"
                value={dashboard?.activeSensors ?? 0}
                label="Sensores activos"
                sublabel="Conectados ahora"
              />
              <KpiCard
                icon={AlertTriangle}
                iconBg="bg-amber-500"
                value={dashboard?.outOfRangeHoursLast24h ?? 0}
                label="Horas en alerta"
                sublabel="Últimas 24h"
              />
              <KpiCard
                icon={Activity}
                iconBg="bg-red-500"
                value={dashboard?.outOfRangeLast24h ?? 0}
                label="Lecturas fuera de rango"
                sublabel="Últimas 24h"
              />
            </div>

            {/* Current readings per sensor */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                  <CircleDot className="h-4 w-4 text-cyan-500" />
                  Lectura actual por sensor
                </h2>
                {loading && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-500">
                    <LoaderPinwheel className="h-3.5 w-3.5 animate-spin" />
                    Actualizando...
                  </span>
                )}
              </div>

              {(dashboard?.latestPerSensor || []).length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Wifi className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No hay lecturas disponibles</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(dashboard?.latestPerSensor || []).map((item) => {
                    const battery = getBatteryFromPayload(item.rawPayload);
                    const isSelected = sensorId === item.sensor;
                    return (
                      <button
                        key={item.sensor}
                        onClick={() => setSensorId(isSelected ? "" : item.sensor)}
                        className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${
                          isSelected
                            ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                            : "border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-gray-900 text-sm leading-tight">
                              {item.sensorName || item.deviceId}
                            </p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{item.deviceId}</p>
                          </div>
                          {isSelected && (
                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                              Seleccionado
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="bg-white rounded-lg p-2.5 border border-red-100">
                            <div className="flex items-center gap-1 mb-0.5">
                              <Thermometer className="h-3.5 w-3.5 text-red-400" />
                              <span className="text-xs text-gray-500">Temp.</span>
                            </div>
                            <p className="text-xl font-bold text-red-600">
                              {item.temperature != null ? item.temperature : "--"}
                              <span className="text-sm font-normal text-red-400"> °C</span>
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-2.5 border border-blue-100">
                            <div className="flex items-center gap-1 mb-0.5">
                              <Droplets className="h-3.5 w-3.5 text-blue-400" />
                              <span className="text-xs text-gray-500">Hum.</span>
                            </div>
                            <p className="text-xl font-bold text-blue-600">
                              {item.humidity != null ? item.humidity : "--"}
                              <span className="text-sm font-normal text-blue-400"> %</span>
                            </p>
                          </div>
                        </div>

                        {battery !== null && <BatteryBar level={battery} />}

                        <p className="text-xs text-gray-400 mt-2">
                          {dayjs(item.measuredAt).format("DD/MM/YYYY HH:mm:ss")}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Chart section */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="h-4 w-4 text-blue-500" />
                <h2 className="font-semibold text-gray-800">Historial de lecturas</h2>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div className="lg:col-span-1">
                  <FormField label="Sensor">
                    <select
                      className={selectCls}
                      value={sensorId}
                      onChange={(e) => setSensorId(e.target.value)}
                    >
                      <option value="">Todos los sensores</option>
                      {sensors.map((sensor) => (
                        <option key={sensor._id} value={sensor._id}>
                          {sensor.name} · {sensor.deviceId}
                        </option>
                      ))}
                    </select>
                  </FormField>
                </div>
                <div>
                  <FormField label="Desde">
                    <input
                      type="datetime-local"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      className={inputCls}
                    />
                  </FormField>
                </div>
                <div>
                  <FormField label="Hasta">
                    <input
                      type="datetime-local"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      className={inputCls}
                    />
                  </FormField>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleFilter}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                    Aplicar
                  </button>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handlePoll}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
                  >
                    <Radio className="h-4 w-4" />
                    Leer ahora
                  </button>
                </div>
              </div>

              {/* Chart */}
              <div className="h-[340px]">
                {points.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400">
                    <Activity className="h-10 w-10 mb-2 opacity-20" />
                    <p className="text-sm">Sin datos en el rango seleccionado</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={points} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="time" minTickGap={24} tick={{ fontSize: 11, fill: "#9ca3af" }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                      {sensorId && (
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                      )}
                      <Tooltip
                        contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: 12 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {sensorId ? (
                        <>
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="temperature"
                            stroke="#ef4444"
                            strokeWidth={2}
                            name="Temperatura (°C)"
                            dot={false}
                            activeDot={{ r: 4 }}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="humidity"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            name="Humedad (%)"
                            dot={false}
                            activeDot={{ r: 4 }}
                          />
                        </>
                      ) : (
                        sensors.map((sensor, index) => (
                          <Line
                            key={sensor._id}
                            yAxisId="left"
                            type="monotone"
                            dataKey={sensor._id}
                            stroke={sensorColors[index % sensorColors.length]}
                            strokeWidth={2}
                            name={`${sensor.name} (${sensor.deviceId})`}
                            dot={false}
                            connectNulls
                          />
                        ))
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Selected sensor detail */}
            {selectedSensor && (
              <div className="bg-white rounded-2xl border border-blue-200 shadow-sm p-5 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{selectedSensor.name}</h3>
                    <p className="text-sm text-gray-500 font-mono">{selectedSensor.deviceId}</p>
                  </div>
                  <button
                    onClick={() => setSensorId("")}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-1.5 mb-1">
                      <MapPin className="h-3.5 w-3.5 text-gray-400" />
                      <p className="text-xs text-gray-500 font-medium">Ubicación</p>
                    </div>
                    <p className="text-sm font-medium text-gray-800">
                      {selectedSensor.cliente?.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedSensor.sede?.name} · {selectedSensor.area?.name}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Clock className="h-3.5 w-3.5 text-gray-400" />
                      <p className="text-xs text-gray-500 font-medium">Frecuencia de lectura</p>
                    </div>
                    <p className="text-lg font-bold text-gray-800">
                      {selectedSensor.pollingIntervalSeconds}
                      <span className="text-sm font-normal text-gray-500">s</span>
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Wifi className="h-3.5 w-3.5 text-gray-400" />
                      <p className="text-xs text-gray-500 font-medium">Estado</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
                      selectedSensor.isActive ? "text-green-700" : "text-gray-500"
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${selectedSensor.isActive ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
                      {selectedSensor.isActive ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Alertas recientes en rango consultado
                  </h4>
                  {sensorAlerts.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl p-3">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      Sin lecturas fuera de rango en el período consultado
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {sensorAlerts.map((item) => (
                        <div
                          key={item._id}
                          className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5 text-sm"
                        >
                          <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                          <span className="text-gray-600">
                            {dayjs(item.measuredAt).format("DD/MM/YYYY HH:mm:ss")}
                          </span>
                          <span className="flex items-center gap-1 text-red-700 font-medium">
                            <Thermometer className="h-3.5 w-3.5" /> {item.temperature}°C
                          </span>
                          <span className="flex items-center gap-1 text-blue-700 font-medium">
                            <Droplets className="h-3.5 w-3.5" /> {item.humidity}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════
            PARAMETRIZACIÓN TAB
        ════════════════════════════════════════════ */}
        {activeTab === "param" && (
          <div className="space-y-5">

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* Credentials card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Settings2 className="h-5 w-5 text-gray-500" />
                    Credenciales Tuya IoT
                  </h3>
                  {credentialsStatus && (
                    <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Configurado
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  <FormField label="Client ID" required>
                    <input
                      className={inputCls}
                      placeholder="Ingresa el Client ID"
                      value={credentialsForm.clientId}
                      disabled={Boolean(credentialsStatus)}
                      onChange={(e) =>
                        setCredentialsForm((prev) => ({ ...prev, clientId: e.target.value }))
                      }
                    />
                  </FormField>

                  <FormField label="Client Secret" required>
                    <input
                      className={inputCls}
                      placeholder="Ingresa el Client Secret"
                      type="password"
                      disabled={Boolean(credentialsStatus)}
                      value={credentialsForm.clientSecret}
                      onChange={(e) =>
                        setCredentialsForm((prev) => ({ ...prev, clientSecret: e.target.value }))
                      }
                    />
                  </FormField>

                  <FormField label="Región">
                    <select
                      className={selectCls}
                      disabled={Boolean(credentialsStatus)}
                      value={credentialsForm.region}
                      onChange={(e) =>
                        setCredentialsForm((prev) => ({ ...prev, region: e.target.value }))
                      }
                    >
                      <option value="us">Estados Unidos (US)</option>
                      <option value="eu">Europa (EU)</option>
                      <option value="in">India (IN)</option>
                      <option value="cn">China (CN)</option>
                    </select>
                  </FormField>
                </div>

                <div className="pt-2">
                  {!credentialsStatus ? (
                    <button
                      onClick={handleSaveCredentials}
                      disabled={loading}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
                    >
                      Guardar configuración
                    </button>
                  ) : (
                    <button
                      onClick={handleDeleteCredentials}
                      disabled={loading}
                      className="flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Eliminar configuración
                    </button>
                  )}
                </div>
              </div>

              {/* Register sensor card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Radio className="h-5 w-5 text-cyan-500" />
                  Registrar nuevo sensor
                </h3>

                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <FormField label="Nombre del sensor" required>
                      <input
                        className={inputCls}
                        placeholder="Ej. Sensor Sala de UCI"
                        value={sensorForm.name}
                        onChange={(e) =>
                          setSensorForm((prev) => ({ ...prev, name: e.target.value }))
                        }
                      />
                    </FormField>
                  </div>

                  <div className="col-span-2">
                    <FormField label="Device ID" required>
                      <input
                        className={inputCls}
                        placeholder="ID del dispositivo Tuya"
                        value={sensorForm.deviceId}
                        onChange={(e) =>
                          setSensorForm((prev) => ({ ...prev, deviceId: e.target.value }))
                        }
                      />
                    </FormField>
                  </div>

                  <div className="col-span-2">
                    <FormField label="Tipo de sensor" required>
                      <select
                        className={selectCls}
                        value={sensorForm.sensorType}
                        onChange={(e) =>
                          setSensorForm((prev) => ({ ...prev, sensorType: e.target.value }))
                        }
                      >
                        {sensorTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <div>
                    <FormField label="Cliente" required>
                      <select
                        className={selectCls}
                        value={sensorForm.cliente}
                        onChange={(e) =>
                          setSensorForm((prev) => ({
                            ...prev,
                            cliente: e.target.value,
                            sede: "",
                            area: "",
                            equipo: "",
                          }))
                        }
                      >
                        <option value="">Selecciona</option>
                        {clients.map((client) => (
                          <option key={client._id} value={client._id}>
                            {client.name}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <div>
                    <FormField label="Sede" required>
                      <select
                        className={selectCls}
                        value={sensorForm.sede}
                        onChange={(e) =>
                          setSensorForm((prev) => ({ ...prev, sede: e.target.value, equipo: "" }))
                        }
                      >
                        <option value="">Selecciona</option>
                        {sedes.map((sede) => (
                          <option key={sede._id} value={sede._id}>
                            {sede.name}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <div>
                    <FormField label="Área" required>
                      <select
                        className={selectCls}
                        value={sensorForm.area}
                        onChange={(e) =>
                          setSensorForm((prev) => ({ ...prev, area: e.target.value, equipo: "" }))
                        }
                      >
                        <option value="">Selecciona</option>
                        {areas.map((area) => (
                          <option key={area._id} value={area._id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <div>
                    <FormField label="Equipo (opcional)">
                      <select
                        className={selectCls}
                        value={sensorForm.equipo}
                        onChange={(e) =>
                          setSensorForm((prev) => ({ ...prev, equipo: e.target.value }))
                        }
                      >
                        <option value="">Sin equipo</option>
                        {equipos.map((equipo) => (
                          <option key={equipo._id} value={equipo._id}>
                            {equipo.serial || equipo.codigo}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <div className="col-span-2">
                    <FormField label="Usuarios para alertas push">
                      <select
                        multiple
                        className={`${selectCls} min-h-[80px]`}
                        value={sensorForm.alertRecipients}
                        onChange={(e) =>
                          setSensorForm((prev) => ({
                            ...prev,
                            alertRecipients: Array.from(e.target.selectedOptions).map(
                              (opt) => opt.value,
                            ),
                          }))
                        }
                      >
                        {alertUsers.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.name} ({user.email})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">Mantén Ctrl/Cmd para seleccionar varios</p>
                    </FormField>
                  </div>
                </div>

                <button
                  onClick={handleRegisterSensor}
                  disabled={loading}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {loading ? (
                    <LoaderPinwheel className="h-4 w-4 animate-spin" />
                  ) : (
                    <Radio className="h-4 w-4" />
                  )}
                  {loading ? "Registrando..." : "Registrar sensor"}
                </button>
              </div>
            </div>

            {/* Sensor list */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Gauge className="h-5 w-5 text-gray-500" />
                  Sensores registrados
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {sensors.length}
                  </span>
                </h3>
              </div>

              {sensors.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Radio className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No hay sensores registrados aún</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sensors.map((sensor) => (
                    <div
                      key={sensor._id}
                      className="border border-gray-200 rounded-xl p-4 space-y-3 hover:border-gray-300 transition-colors"
                    >
                      {/* Sensor header */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900">{sensor.name}</p>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                              {sensorTypes.find((t) => t.value === sensor.sensorType)?.label || sensor.sensorType}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                              sensor.isActive
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-gray-100 text-gray-600 border border-gray-200"
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sensor.isActive ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
                              {sensor.isActive ? "Activo" : "Inactivo"}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">
                            <span className="font-mono">{sensor.deviceId}</span>
                            {" · "}
                            {sensor.cliente?.name} / {sensor.sede?.name} / {sensor.area?.name}
                          </p>
                        </div>
                      </div>

                      {/* Alert ranges (temp/humidity sensors) */}
                      {sensor.sensorType === "temperature_humidity" && (
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 space-y-3">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Bell className={`h-4 w-4 ${sensor.alertsEnabled ? "text-amber-500" : "text-gray-400"}`} />
                                <span className="text-xs font-medium text-gray-700">
                                  Alertas: {sensor.alertsEnabled ? "Activas" : "Desactivadas"}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500">
                                Temp: {sensor.alertRanges?.temperature?.min ?? 18}°C — {sensor.alertRanges?.temperature?.max ?? 27}°C
                                {" · "}
                                Humedad: {sensor.alertRanges?.humidity?.min ?? 35}% — {sensor.alertRanges?.humidity?.max ?? 65}%
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleUpdateAlerts(sensor)}
                                className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                              >
                                <Settings2 className="h-3.5 w-3.5" />
                                Modificar rangos
                              </button>
                              {sensor.alertsEnabled && (
                                <button
                                  onClick={() => handleRemoveAlerts(sensor)}
                                  className="flex items-center gap-1.5 border border-gray-200 text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                                >
                                  <BellOff className="h-3.5 w-3.5" />
                                  Desactivar
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Recipients */}
                          <div>
                            <p className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                              <Users className="h-3.5 w-3.5" />
                              Usuarios para notificación push
                              {loadingAlerts && <LoaderPinwheel className="h-3 w-3 animate-spin text-blue-500" />}
                            </p>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {(sensor.alertRecipients || []).length === 0 ? (
                                <span className="text-xs text-gray-400 italic">Sin usuarios asignados</span>
                              ) : (
                                (sensor.alertRecipients || []).map((user) => (
                                  <span
                                    key={user._id || user}
                                    className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-700 border border-blue-200 px-2.5 py-1 text-xs font-medium"
                                  >
                                    {user.name || user}
                                    <button
                                      onClick={() =>
                                        handleUpdateRecipients(
                                          sensor,
                                          (sensor.alertRecipients || [])
                                            .map((u) => u._id || u)
                                            .filter((id) => id !== (user._id || user)),
                                        )
                                      }
                                      disabled={loadingAlerts}
                                      className="text-blue-500 hover:text-blue-800 ml-0.5"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </span>
                                ))
                              )}
                            </div>
                            <select
                              className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                              value=""
                              disabled={loadingAlerts}
                              onChange={(e) => {
                                const userId = e.target.value;
                                if (!userId) return;
                                const current = (sensor.alertRecipients || []).map((u) => u._id || u);
                                if (current.includes(userId)) return;
                                handleUpdateRecipients(sensor, [...current, userId]);
                              }}
                            >
                              <option value="">+ Agregar usuario</option>
                              {alertUsers
                                .filter(
                                  (user) =>
                                    !(sensor.alertRecipients || [])
                                      .map((u) => u._id || u)
                                      .includes(user._id),
                                )
                                .map((user) => (
                                  <option key={user._id} value={user._id}>
                                    {user.name} ({user.email})
                                  </option>
                                ))}
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Footer actions */}
                      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-100 rounded-lg px-2.5 py-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            Frecuencia: {sensor.pollingIntervalSeconds || 10}s
                          </span>
                          <button
                            onClick={() => handleUpdatePollingInterval(sensor)}
                            className="flex items-center gap-1.5 border border-gray-200 text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                          >
                            <Settings2 className="h-3.5 w-3.5" />
                            Cambiar frecuencia
                          </button>
                          <button
                            onClick={() => handleToggleSensorReading(sensor)}
                            disabled={loadingActive}
                            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-colors disabled:opacity-60 ${
                              sensor.isActive
                                ? "bg-amber-500 hover:bg-amber-600"
                                : "bg-green-600 hover:bg-green-700"
                            }`}
                          >
                            {loadingActive ? (
                              <LoaderPinwheel className="h-3.5 w-3.5 animate-spin" />
                            ) : sensor.isActive ? (
                              <WifiOff className="h-3.5 w-3.5" />
                            ) : (
                              <Wifi className="h-3.5 w-3.5" />
                            )}
                            {loadingActive
                              ? "Cargando..."
                              : sensor.isActive
                              ? "Detener lecturas"
                              : "Iniciar lecturas"}
                          </button>
                        </div>

                        <button
                          onClick={() => setDeleteModalSensor(sensor)}
                          className="flex items-center gap-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-200 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Delete Modal ── */}
      <Transition appear show={Boolean(deleteModalSensor)} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteModalSensor(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
                <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <Dialog.Title className="font-semibold text-gray-900">
                      Eliminar sensor
                    </Dialog.Title>
                    <p className="text-xs text-red-600 mt-0.5">Esta acción es irreversible</p>
                  </div>
                </div>

                <div className="px-6 py-5 space-y-4">
                  <p className="text-sm text-gray-600">
                    Eliminarás el sensor <strong>{deleteModalSensor?.name}</strong> y{" "}
                    <strong>todo su historial de lecturas</strong> permanentemente.
                  </p>

                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <p className="text-sm text-red-700 font-medium mb-2">
                      Escribe <span className="font-mono font-bold">confirmar</span> para continuar
                    </p>
                    <input
                      className="w-full border border-red-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
                      placeholder='confirmar'
                      value={deleteConfirmWord[deleteModalSensor?._id] || ""}
                      onChange={(e) =>
                        setDeleteConfirmWord((prev) => ({
                          ...prev,
                          [deleteModalSensor?._id]: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-1">
                    <button
                      onClick={() => setDeleteModalSensor(null)}
                      className="border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() =>
                        deleteModalSensor && handleDeleteSensor(deleteModalSensor._id)
                      }
                      disabled={loadingDelete}
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
                    >
                      {loadingDelete && <LoaderPinwheel className="h-4 w-4 animate-spin" />}
                      {loadingDelete ? "Eliminando..." : "Confirmar eliminación"}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default EnvironmentalMonitoringPage;
